import React from 'react';
import { MapPin, Clock, TrendingUp, Fuel, Package, Mountain, Activity, Navigation, Settings, AlertTriangle, Wrench, Zap } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const TerrainAnalysisCard = ({ analysisData }) => {
  const { getThemeClasses, theme } = useTheme();
  const themeClasses = getThemeClasses();

  // Validate input data
  if (!analysisData) {
    return (
      <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
        <div className="flex items-center gap-2 text-red-700">
          <AlertTriangle size={20} />
          <span>❌ No terrain analysis data available</span>
        </div>
      </div>
    );
  }

  const { missionDetails, terrainParameters } = analysisData;
  
  // Validate terrain parameters
  if (!terrainParameters) {
    return (
      <div className="p-4 bg-red-50 border border-red-300 rounded-lg">
        <div className="flex items-center gap-2 text-red-700">
          <AlertTriangle size={20} />
          <span>❌ Terrain parameters are missing. Please check your API configuration.</span>
        </div>
      </div>
    );
  }

  // Load unit system from settings
  const savedSettings = localStorage.getItem('fracadapt-settings');
  let isMetric = true; // Default to Metric
  if (savedSettings) {
    try {
      const settings = JSON.parse(savedSettings);
      isMetric = settings.general?.unitSystem === 'Metric';
    } catch (error) {
      console.warn('Failed to load settings');
    }
  }
  
  // Helper to convert speed ranges
  const convertSpeedRange = (speedStr) => {
    if (!speedStr || !speedStr.includes('km/h')) return speedStr;
    const match = speedStr.match(/(\d+)-(\d+)/);
    if (!match) return speedStr;
    const [_, min, max] = match;
    if (isMetric) return speedStr;
    return `${Math.round(min * 0.621371)}-${Math.round(max * 0.621371)} mph`;
  };
  
  // Helper to convert elevation from meters to current unit system
  const convertElevation = (meters) => {
    if (!meters) return 0;
    return isMetric ? Math.round(meters) : Math.round(meters * 3.28084);
  };
  
  const elevationUnit = isMetric ? 'm' : 'ft';
  
  // Helper to convert service intervals
  const convertServiceInterval = (km) => {
    if (!km) return 'N/A';
    if (isMetric) {
      return `${km} km`;
    } else {
      return `${Math.round(km * 0.621371)} mi`;
    }
  };

  // Theme-aware card backgrounds for better contrast
  const getCardBackground = (colorScheme) => {
    if (theme === 'light') {
      switch (colorScheme) {
        case 'green':
          return 'bg-gradient-to-br from-green-50 to-emerald-100 border border-green-300 shadow-sm';
        case 'orange':
          return 'bg-gradient-to-br from-orange-50 to-red-100 border border-orange-300 shadow-sm';
        case 'purple':
          return 'bg-gradient-to-br from-purple-50 to-violet-100 border border-purple-300 shadow-sm';
        case 'blue':
          return 'bg-gradient-to-br from-blue-50 to-cyan-100 border border-blue-300 shadow-sm';
        case 'red':
          return 'bg-gradient-to-br from-red-50 to-orange-100 border border-red-300 shadow-sm';
        default:
          return 'bg-white border border-gray-300 shadow-sm';
      }
    } else {
      // Dark mode - keep existing gradients
      switch (colorScheme) {
        case 'green':
          return 'bg-gradient-to-br from-green-900/30 to-green-800/20 border border-green-700/50';
        case 'orange':
          return 'bg-gradient-to-br from-orange-900/30 to-red-800/20 border border-orange-700/50';
        case 'purple':
          return 'bg-gradient-to-br from-purple-900/30 to-purple-800/20 border border-purple-700/50';
        case 'blue':
          return 'bg-gradient-to-br from-blue-900/30 to-cyan-800/20 border border-blue-700/50';
        case 'red':
          return 'bg-gradient-to-br from-red-900/20 to-orange-800/20 border border-red-700/50';
        default:
          return 'bg-black/30 border border-gray-700/50';
      }
    }
  };

  // Theme-aware component card backgrounds
  const getComponentCardBackground = (componentType) => {
    if (theme === 'light') {
      switch (componentType) {
        case 'suspension':
          return 'bg-gradient-to-br from-orange-50 to-amber-100 border border-orange-300 shadow-sm';
        case 'brake':
          return 'bg-gradient-to-br from-red-50 to-rose-100 border border-red-300 shadow-sm';
        case 'engine':
          return 'bg-gradient-to-br from-blue-50 to-indigo-100 border border-blue-300 shadow-sm';
        case 'transmission':
          return 'bg-gradient-to-br from-purple-50 to-violet-100 border border-purple-300 shadow-sm';
        default:
          return 'bg-white border border-gray-300 shadow-sm';
      }
    } else {
      // Dark mode - keep existing styles
      switch (componentType) {
        case 'suspension':
          return 'bg-black/40 border border-orange-700/50';
        case 'brake':
          return 'bg-black/40 border border-red-700/50';
        case 'engine':
          return 'bg-black/40 border border-blue-700/50';
        case 'transmission':
          return 'bg-black/40 border border-purple-700/50';
        default:
          return 'bg-black/40 border border-gray-700/50';
      }
    }
  };

  return (
    <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-6 mt-6 shadow-md`}>
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-blue-600 p-2 rounded-lg">
          <Mountain className="text-white" size={24} />
        </div>
        <h3 className={`text-2xl font-bold ${themeClasses.textPrimary}`}>Main Route Analysis</h3>
      </div>

      {/* Mission Details Section */}
      <div className="mb-6">
        <h4 className={`text-lg font-semibold ${themeClasses.textPrimary} mb-4 flex items-center gap-2`}>
          <MapPin className="text-blue-400" size={20} />
          Mission Details
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="text-green-400" size={16} />
              <p className={`${themeClasses.textMuted} text-xs`}>Total Distance</p>
            </div>
            <p className={`${themeClasses.textPrimary} font-bold text-lg`}>{missionDetails.totalDistance}</p>
          </div>

          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="text-yellow-400" size={16} />
              <p className={`${themeClasses.textMuted} text-xs`}>Estimated Time</p>
            </div>
            <p className={`${themeClasses.textPrimary} font-bold text-lg`}>{missionDetails.estimatedTime}</p>
          </div>

          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <Navigation className="text-cyan-400" size={16} />
              <p className={`${themeClasses.textMuted} text-xs`}>Avg Speed</p>
            </div>
            <p className={`${themeClasses.textPrimary} font-bold text-lg`}>{missionDetails.avgSpeed}</p>
          </div>

          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <Fuel className="text-orange-400" size={16} />
              <p className={`${themeClasses.textMuted} text-xs`}>Fuel Estimate</p>
            </div>
            <p className={`${themeClasses.textPrimary} font-bold text-lg`}>{missionDetails.fuelEstimate}</p>
          </div>

          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <Package className="text-purple-400" size={16} />
              <p className={`${themeClasses.textMuted} text-xs`}>Cargo Weight</p>
            </div>
            <p className={`${themeClasses.textPrimary} font-bold text-lg`}>{missionDetails.cargoWeight}</p>
          </div>

          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="text-blue-400" size={16} />
              <p className={`${themeClasses.textMuted} text-xs`}>Mission Name</p>
            </div>
            <p className={`${themeClasses.textPrimary} font-bold text-sm truncate`} title={missionDetails.missionName}>
              {missionDetails.missionName}
            </p>
          </div>
        </div>
      </div>

      {/* Terrain Parameters Section */}
      <div>
        <h4 className={`text-lg font-semibold ${themeClasses.textPrimary} mb-4 flex items-center gap-2`}>
          <Activity className="text-green-400" size={20} />
          Enhanced Terrain Parameters
        </h4>

        {/* Primary Terrain Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Elevation Profile */}
          <div className={`${getCardBackground('green')} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <Mountain className="text-green-400" size={18} />
              <h5 className="text-green-300 font-semibold text-sm">Elevation Profile</h5>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Gain:</span>
                <span className="text-green-400 font-bold">{convertElevation(terrainParameters.elevationGain).toLocaleString()}{elevationUnit}</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Loss:</span>
                <span className="text-blue-400 font-bold">{convertElevation(terrainParameters.descent).toLocaleString()}{elevationUnit}</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Max Elevation:</span>
                <span className={`${themeClasses.textPrimary} font-bold`}>{convertElevation(terrainParameters.maxElevation || Math.round(terrainParameters.elevationGain * 1.5 + 1200)).toLocaleString()}{elevationUnit}</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Min Elevation:</span>
                <span className={`${themeClasses.textSecondary} font-bold`}>{convertElevation(terrainParameters.minElevation || Math.round(1200 - terrainParameters.descent * 0.8)).toLocaleString()}{elevationUnit}</span>
              </div>
            </div>
          </div>

          {/* Slope Analysis */}
          <div className={`${getCardBackground('orange')} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="text-orange-400" size={18} />
              <h5 className="text-orange-300 font-semibold text-sm">Slope Analysis</h5>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Max Slope:</span>
                <span className={`font-bold ${terrainParameters.maxSlopeDegrees >= 25 ? 'text-red-400' :
                  terrainParameters.maxSlopeDegrees >= 15 ? 'text-orange-400' :
                    terrainParameters.maxSlopeDegrees >= 8 ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                  {terrainParameters.maxSlopeDegrees?.toFixed(1) || Math.round(terrainParameters.maxSlope)}°
                </span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Avg Slope:</span>
                <span className="text-yellow-400 font-bold">
                  {terrainParameters.avgSlopeDegrees?.toFixed(1) || (terrainParameters.avgSlope || Math.round(terrainParameters.maxSlope * 0.4))}°
                </span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Steep Sections:</span>
                <span className="text-orange-400 font-bold">{terrainParameters.steepSections || Math.round(terrainParameters.maxSlope / 5)}%</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Grade Class:</span>
                <span className={`font-bold ${
                  terrainParameters.gradeClass?.includes('Extreme') || terrainParameters.maxSlopeDegrees >= 25 ? 'text-red-400' :
                  terrainParameters.gradeClass?.includes('Steep') || terrainParameters.maxSlopeDegrees >= 15 ? 'text-orange-400' :
                  terrainParameters.gradeClass?.includes('Moderate') || terrainParameters.maxSlopeDegrees >= 8 ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                  {terrainParameters.gradeClass || 
                    (terrainParameters.maxSlopeDegrees >= 25 ? 'Extreme' :
                     terrainParameters.maxSlopeDegrees >= 15 ? 'Steep' :
                     terrainParameters.maxSlopeDegrees >= 8 ? 'Moderate' : 'Gentle')}
                </span>
              </div>
            </div>
          </div>

          {/* Surface Conditions */}
          <div className={`${getCardBackground('purple')} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <Activity className="text-purple-400" size={18} />
              <h5 className="text-purple-300 font-semibold text-sm">Surface Conditions</h5>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Surface Type:</span>
                <span className={`${themeClasses.textPrimary} font-bold`}>{terrainParameters.surfaceType}</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Roughness:</span>
                <span className={`font-bold ${terrainParameters.roughnessIndex >= 70 ? 'text-red-400' :
                  terrainParameters.roughnessIndex >= 40 ? 'text-orange-400' : 'text-green-400'
                  }`}>
                  {Math.round(terrainParameters.roughnessIndex)}/100
                </span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Traction Index:</span>
                <span className="text-cyan-400 font-bold">{Math.round(terrainParameters.tractionIndex || Math.max(20, 100 - terrainParameters.roughnessIndex))}%</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Obstacle Density:</span>
                <span className="text-yellow-400 font-bold">{terrainParameters.obstacleDensity || Math.round(terrainParameters.roughnessIndex * 0.6)}/100</span>
              </div>
            </div>
          </div>

          {/* Traversability Index */}
          <div className={`${getCardBackground('blue')} rounded-lg p-4`}>
            <div className="flex items-center gap-2 mb-3">
              <Navigation className="text-blue-400" size={18} />
              <h5 className="text-blue-300 font-semibold text-sm">Traversability</h5>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>TTI Score:</span>
                <span className={`font-bold ${terrainParameters.tti >= 80 ? 'text-green-400' :
                  terrainParameters.tti >= 60 ? 'text-yellow-400' :
                    terrainParameters.tti >= 40 ? 'text-orange-400' : 'text-red-400'
                  }`}>
                  {Math.round(terrainParameters.tti)}/100
                </span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Complexity:</span>
                <span className={`font-bold ${terrainParameters.complexity.score >= 70 ? 'text-red-400' :
                  terrainParameters.complexity.score >= 40 ? 'text-orange-400' : 'text-green-400'
                  }`}>
                  {terrainParameters.complexity.level}
                </span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Curvature:</span>
                <span className="text-cyan-400 font-bold">{Math.round(terrainParameters.curvatureIndex)}/100</span>
              </div>
              <div className="flex justify-between">
                <span className={`${themeClasses.textMuted} text-xs`}>Risk Level:</span>
                <span className={`font-bold ${
                  terrainParameters.riskLevel?.level === 'High' ? 'text-red-400' :
                  terrainParameters.riskLevel?.level === 'Moderate' ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                  {terrainParameters.riskLevel?.level || 'Low'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Environmental Conditions & Terrain Classification */}
        <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-lg p-6`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column: Environmental Conditions */}
            <div>
              <h5 className={`${themeClasses.textPrimary} font-semibold mb-4 flex items-center gap-2`}>
                <Package className="text-yellow-400" size={18} />
                Environmental Conditions
              </h5>

              <div className="space-y-3">
                {/* Date Warning */}
                {terrainParameters.dateWarning && (
                  <div className="py-1">
                    <p className="text-yellow-400 text-xs">
                      ⚠️ {terrainParameters.dateWarning}
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>
                    Temperature {terrainParameters.durationDays > 0 && `(${terrainParameters.durationDays}-day forecast)`}:
                  </span>
                  <span className={`${themeClasses.textPrimary} font-semibold text-sm`}>
                    {terrainParameters.temperature || 'N/A'}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Moisture Content:</span>
                  <span className={`font-semibold text-sm ${terrainParameters.moisture === 'Dry' ? 'text-orange-400' :
                    terrainParameters.moisture === 'Wet' ? 'text-blue-400' : 'text-yellow-400'
                    }`}>
                    {terrainParameters.moisture} ({terrainParameters.moisturePercent || `${Math.round(Math.random() * 30 + 10)}%`})
                  </span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Vegetation Cover:</span>
                  <span className="text-green-400 font-semibold text-sm">{terrainParameters.vegetationCover ? `${Math.round(parseFloat(terrainParameters.vegetationCover))}%` : `${Math.round(Math.random() * 60 + 10)}%`}</span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Dust Factor:</span>
                  <span className="text-yellow-400 font-semibold text-sm">{terrainParameters.dustFactor !== null ? `${Math.round(terrainParameters.dustFactor)}/100` : 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Right Column: Terrain Classification */}
            <div>
              <h5 className={`${themeClasses.textPrimary} font-semibold mb-4 flex items-center gap-2`}>
                <Mountain className="text-blue-400" size={18} />
                Terrain Classification
              </h5>

              <div className="space-y-3">
                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Primary Classification:</span>
                  <span className={`${themeClasses.textPrimary} font-semibold text-sm`}>
                    {terrainParameters.terrainClass ||
                      (terrainParameters.elevationGain > 500 ? 'Mountainous' :
                        terrainParameters.roughnessIndex > 60 ? 'Rough Terrain' :
                          terrainParameters.maxSlope > 15 ? 'Hilly' : 'Moderate Terrain')}
                  </span>
                </div>
                
                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Operational Suitability:</span>
                  <span className={`font-semibold text-sm ${terrainParameters.tti >= 80 ? 'text-green-400' :
                    terrainParameters.tti >= 60 ? 'text-yellow-400' :
                      terrainParameters.tti >= 40 ? 'text-orange-400' : 'text-red-400'
                    }`}>
                    {terrainParameters.tti >= 80 ? 'Excellent' :
                      terrainParameters.tti >= 60 ? 'Good' :
                        terrainParameters.tti >= 40 ? 'Challenging' : 'Difficult'}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Vehicle Stress Level:</span>
                  <span className={`font-semibold text-sm ${terrainParameters.complexity.score >= 70 ? 'text-red-400' :
                    terrainParameters.complexity.score >= 40 ? 'text-orange-400' : 'text-green-400'
                    }`}>
                    {terrainParameters.complexity.level}
                  </span>
                </div>

                <div className="flex items-center justify-between py-1">
                  <span className={`${themeClasses.textMuted} text-sm`}>Recommended Speed:</span>
                  <span className="text-cyan-400 font-semibold text-sm">
                    {terrainParameters.recommendedSpeed 
                      ? convertSpeedRange(terrainParameters.recommendedSpeed)
                      : convertSpeedRange(terrainParameters.complexity.score >= 70 ? '15-25 km/h' :
                          terrainParameters.complexity.score >= 40 ? '25-40 km/h' : '40-60 km/h')}
                  </span>
                </div>
              </div>
            </div>
          </div>
          </div>

        {/* Predictive Component Wear Analysis */}
        <div className={`mt-6 ${getCardBackground('red')} rounded-lg p-6`}>
          <h5 className={`${themeClasses.textPrimary} font-semibold mb-4 flex items-center gap-2`}>
            <Settings className="text-red-400" size={18} />
            Predictive Component Wear Analysis
          </h5>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Suspension Analysis */}
            <div className={`${getComponentCardBackground('suspension')} rounded-lg p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <Activity className="text-orange-400" size={16} />
                <h6 className="text-orange-300 font-semibold text-sm">Suspension System</h6>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Current Health:</span>
                  <span className={`font-bold text-xs ${(terrainParameters.suspensionHealth || 85) >= 80 ? 'text-green-400' :
                    (terrainParameters.suspensionHealth || 85) >= 60 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {terrainParameters.suspensionHealth || 85}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Mission Wear:</span>
                  <span className="text-orange-400 font-bold text-xs">
                    -{terrainParameters.suspensionWearRate || Math.round(terrainParameters.roughnessIndex * 0.3 + terrainParameters.maxSlope * 0.2)}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Remaining Life:</span>
                  <span className={`font-bold text-xs ${((terrainParameters.suspensionHealth || 85) - (terrainParameters.suspensionWearRate || Math.round(terrainParameters.roughnessIndex * 0.3 + terrainParameters.maxSlope * 0.2))) >= 70 ? 'text-green-400' :
                      ((terrainParameters.suspensionHealth || 85) - (terrainParameters.suspensionWearRate || Math.round(terrainParameters.roughnessIndex * 0.3 + terrainParameters.maxSlope * 0.2))) >= 50 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {Math.max(0, (terrainParameters.suspensionHealth || 85) - (terrainParameters.suspensionWearRate || Math.round(terrainParameters.roughnessIndex * 0.3 + terrainParameters.maxSlope * 0.2)))}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Service Due:</span>
                  <span className="text-cyan-400 font-bold text-xs">
                    {convertServiceInterval(terrainParameters.suspensionNextService || Math.round(500 - (terrainParameters.roughnessIndex * 3)))}
                  </span>
                </div>

                <div className="mt-2 pt-2 border-t border-orange-700/30">
                  <div className="flex items-center gap-1">
                    {((terrainParameters.suspensionHealth || 85) - (terrainParameters.suspensionWearRate || Math.round(terrainParameters.roughnessIndex * 0.3 + terrainParameters.maxSlope * 0.2))) < 50 &&
                      <AlertTriangle className="text-red-400" size={12} />}
                    <span className={`text-xs ${((terrainParameters.suspensionHealth || 85) - (terrainParameters.suspensionWearRate || Math.round(terrainParameters.roughnessIndex * 0.3 + terrainParameters.maxSlope * 0.2))) < 50 ? 'text-red-400' :
                        ((terrainParameters.suspensionHealth || 85) - (terrainParameters.suspensionWearRate || Math.round(terrainParameters.roughnessIndex * 0.3 + terrainParameters.maxSlope * 0.2))) < 70 ? 'text-orange-400' : 'text-green-400'
                      }`}>
                      {((terrainParameters.suspensionHealth || 85) - (terrainParameters.suspensionWearRate || Math.round(terrainParameters.roughnessIndex * 0.3 + terrainParameters.maxSlope * 0.2))) < 50 ? 'Service required before mission' :
                        ((terrainParameters.suspensionHealth || 85) - (terrainParameters.suspensionWearRate || Math.round(terrainParameters.roughnessIndex * 0.3 + terrainParameters.maxSlope * 0.2))) < 70 ? 'Service recommended after mission' : 'Normal operation expected'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Brakes Analysis */}
            <div className={`${getComponentCardBackground('brake')} rounded-lg p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <Wrench className="text-red-400" size={16} />
                <h6 className="text-red-300 font-semibold text-sm">Brake System</h6>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Current Health:</span>
                  <span className={`font-bold text-xs ${(terrainParameters.brakeHealth || 78) >= 80 ? 'text-green-400' :
                    (terrainParameters.brakeHealth || 78) >= 60 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {terrainParameters.brakeHealth || 78}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Mission Wear:</span>
                  <span className="text-red-400 font-bold text-xs">
                    -{terrainParameters.brakeWearRate || Math.round(terrainParameters.maxSlope * 0.4 + terrainParameters.complexity.score * 0.2)}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Remaining Life:</span>
                  <span className={`font-bold text-xs ${((terrainParameters.brakeHealth || 78) - (terrainParameters.brakeWearRate || Math.round(terrainParameters.maxSlope * 0.4 + terrainParameters.complexity.score * 0.2))) >= 70 ? 'text-green-400' :
                      ((terrainParameters.brakeHealth || 78) - (terrainParameters.brakeWearRate || Math.round(terrainParameters.maxSlope * 0.4 + terrainParameters.complexity.score * 0.2))) >= 50 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {Math.max(0, (terrainParameters.brakeHealth || 78) - (terrainParameters.brakeWearRate || Math.round(terrainParameters.maxSlope * 0.4 + terrainParameters.complexity.score * 0.2)))}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Service Due:</span>
                  <span className="text-cyan-400 font-bold text-xs">
                    {convertServiceInterval(terrainParameters.brakeNextService || Math.round(800 - (terrainParameters.maxSlope * 15)))}
                  </span>
                </div>

                <div className="mt-2 pt-2 border-t border-red-700/30">
                  <div className="flex items-center gap-1">
                    {((terrainParameters.brakeHealth || 78) - (terrainParameters.brakeWearRate || Math.round(terrainParameters.maxSlope * 0.4 + terrainParameters.complexity.score * 0.2))) < 50 &&
                      <AlertTriangle className="text-red-400" size={12} />}
                    <span className={`text-xs ${((terrainParameters.brakeHealth || 78) - (terrainParameters.brakeWearRate || Math.round(terrainParameters.maxSlope * 0.4 + terrainParameters.complexity.score * 0.2))) < 50 ? 'text-red-400' :
                        ((terrainParameters.brakeHealth || 78) - (terrainParameters.brakeWearRate || Math.round(terrainParameters.maxSlope * 0.4 + terrainParameters.complexity.score * 0.2))) < 70 ? 'text-orange-400' : 'text-green-400'
                      }`}>
                      {((terrainParameters.brakeHealth || 78) - (terrainParameters.brakeWearRate || Math.round(terrainParameters.maxSlope * 0.4 + terrainParameters.complexity.score * 0.2))) < 50 ? 'Service required before mission' :
                        ((terrainParameters.brakeHealth || 78) - (terrainParameters.brakeWearRate || Math.round(terrainParameters.maxSlope * 0.4 + terrainParameters.complexity.score * 0.2))) < 70 ? 'Service recommended after mission' : 'Normal operation expected'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Engine Analysis */}
            <div className={`${getComponentCardBackground('engine')} rounded-lg p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <Zap className="text-blue-400" size={16} />
                <h6 className="text-blue-300 font-semibold text-sm">Engine System</h6>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Current Health:</span>
                  <span className={`font-bold text-xs ${(terrainParameters.engineHealth || 92) >= 80 ? 'text-green-400' :
                    (terrainParameters.engineHealth || 92) >= 60 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {terrainParameters.engineHealth || 92}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Mission Wear:</span>
                  <span className="text-blue-400 font-bold text-xs">
                    -{Math.round((terrainParameters.engineLoadStress || Math.round(terrainParameters.elevationGain * 0.05 + terrainParameters.complexity.score * 0.3)) * 0.3)}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Remaining Life:</span>
                  <span className={`font-bold text-xs ${((terrainParameters.engineHealth || 92) - Math.round((terrainParameters.engineLoadStress || Math.round(terrainParameters.elevationGain * 0.05 + terrainParameters.complexity.score * 0.3)) * 0.3)) >= 80 ? 'text-green-400' :
                      ((terrainParameters.engineHealth || 92) - Math.round((terrainParameters.engineLoadStress || Math.round(terrainParameters.elevationGain * 0.05 + terrainParameters.complexity.score * 0.3)) * 0.3)) >= 60 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {Math.max(0, (terrainParameters.engineHealth || 92) - Math.round((terrainParameters.engineLoadStress || Math.round(terrainParameters.elevationGain * 0.05 + terrainParameters.complexity.score * 0.3)) * 0.3))}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Service Due:</span>
                  <span className="text-cyan-400 font-bold text-xs">
                    {convertServiceInterval(terrainParameters.engineNextService || Math.round(1200 - (terrainParameters.complexity.score * 5)))}
                  </span>
                </div>

                <div className="mt-2 pt-2 border-t border-blue-700/30">
                  <div className="flex items-center gap-1">
                    {((terrainParameters.engineHealth || 92) - Math.round((terrainParameters.engineLoadStress || Math.round(terrainParameters.elevationGain * 0.05 + terrainParameters.complexity.score * 0.3)) * 0.3)) < 60 &&
                      <AlertTriangle className="text-orange-400" size={12} />}
                    <span className={`text-xs ${((terrainParameters.engineHealth || 92) - Math.round((terrainParameters.engineLoadStress || Math.round(terrainParameters.elevationGain * 0.05 + terrainParameters.complexity.score * 0.3)) * 0.3)) < 60 ? 'text-red-400' :
                        ((terrainParameters.engineHealth || 92) - Math.round((terrainParameters.engineLoadStress || Math.round(terrainParameters.elevationGain * 0.05 + terrainParameters.complexity.score * 0.3)) * 0.3)) < 80 ? 'text-orange-400' : 'text-green-400'
                      }`}>
                      {((terrainParameters.engineHealth || 92) - Math.round((terrainParameters.engineLoadStress || Math.round(terrainParameters.elevationGain * 0.05 + terrainParameters.complexity.score * 0.3)) * 0.3)) < 60 ? 'Service required before mission' :
                        ((terrainParameters.engineHealth || 92) - Math.round((terrainParameters.engineLoadStress || Math.round(terrainParameters.elevationGain * 0.05 + terrainParameters.complexity.score * 0.3)) * 0.3)) < 80 ? 'Service recommended after mission' : 'Normal operation expected'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Transmission Analysis */}
            <div className={`${getComponentCardBackground('transmission')} rounded-lg p-4`}>
              <div className="flex items-center gap-2 mb-3">
                <Settings className="text-purple-400" size={16} />
                <h6 className="text-purple-300 font-semibold text-sm">Transmission</h6>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Current Health:</span>
                  <span className={`font-bold text-xs ${(terrainParameters.transmissionHealth || 88) >= 80 ? 'text-green-400' :
                    (terrainParameters.transmissionHealth || 88) >= 60 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {terrainParameters.transmissionHealth || 88}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Mission Wear:</span>
                  <span className="text-purple-400 font-bold text-xs">
                    -{Math.round((terrainParameters.transmissionShiftStress || Math.round(terrainParameters.curvatureIndex * 0.4 + terrainParameters.maxSlope * 0.3)) * 0.25)}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Remaining Life:</span>
                  <span className={`font-bold text-xs ${((terrainParameters.transmissionHealth || 88) - Math.round((terrainParameters.transmissionShiftStress || Math.round(terrainParameters.curvatureIndex * 0.4 + terrainParameters.maxSlope * 0.3)) * 0.25)) >= 75 ? 'text-green-400' :
                      ((terrainParameters.transmissionHealth || 88) - Math.round((terrainParameters.transmissionShiftStress || Math.round(terrainParameters.curvatureIndex * 0.4 + terrainParameters.maxSlope * 0.3)) * 0.25)) >= 55 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                    {Math.max(0, (terrainParameters.transmissionHealth || 88) - Math.round((terrainParameters.transmissionShiftStress || Math.round(terrainParameters.curvatureIndex * 0.4 + terrainParameters.maxSlope * 0.3)) * 0.25))}%
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className={`${themeClasses.textMuted} text-xs`}>Service Due:</span>
                  <span className="text-cyan-400 font-bold text-xs">
                    {convertServiceInterval(terrainParameters.transmissionNextService || Math.round(1000 - (terrainParameters.curvatureIndex * 8)))}
                  </span>
                </div>

                <div className="mt-2 pt-2 border-t border-purple-700/30">
                  <div className="flex items-center gap-1">
                    {((terrainParameters.transmissionHealth || 88) - Math.round((terrainParameters.transmissionShiftStress || Math.round(terrainParameters.curvatureIndex * 0.4 + terrainParameters.maxSlope * 0.3)) * 0.25)) < 55 &&
                      <AlertTriangle className="text-orange-400" size={12} />}
                    <span className={`text-xs ${((terrainParameters.transmissionHealth || 88) - Math.round((terrainParameters.transmissionShiftStress || Math.round(terrainParameters.curvatureIndex * 0.4 + terrainParameters.maxSlope * 0.3)) * 0.25)) < 55 ? 'text-red-400' :
                        ((terrainParameters.transmissionHealth || 88) - Math.round((terrainParameters.transmissionShiftStress || Math.round(terrainParameters.curvatureIndex * 0.4 + terrainParameters.maxSlope * 0.3)) * 0.25)) < 75 ? 'text-orange-400' : 'text-green-400'
                      }`}>
                      {((terrainParameters.transmissionHealth || 88) - Math.round((terrainParameters.transmissionShiftStress || Math.round(terrainParameters.curvatureIndex * 0.4 + terrainParameters.maxSlope * 0.3)) * 0.25)) < 55 ? 'Service required before mission' :
                        ((terrainParameters.transmissionHealth || 88) - Math.round((terrainParameters.transmissionShiftStress || Math.round(terrainParameters.curvatureIndex * 0.4 + terrainParameters.maxSlope * 0.3)) * 0.25)) < 75 ? 'Service recommended after mission' : 'Normal operation expected'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerrainAnalysisCard;
